import mammoth from 'mammoth';

export interface ParsedChapter {
  title: string;
  content: string;
  sectionType: 'chapter' | 'prologue' | 'epilogue' | 'introduction' | 'preface';
}

export interface ParsedBook {
  chapters: ParsedChapter[];
  coverImage?: string;
  title?: string;
  author?: string;
}

// Chapter detection patterns
const chapterPatterns = [
  /^chapter\s+(\d+|[ivxlcdm]+)\s*[:\-–—]?\s*/i,
  /^ch\.?\s*(\d+)\s*[:\-–—]?\s*/i,
  /^(\d+)\.\s+/,
  /^part\s+(\d+|[ivxlcdm]+)\s*[:\-–—]?\s*/i,
];

const sectionPatterns: Record<string, RegExp> = {
  prologue: /^prologue\s*[:\-–—]?\s*/i,
  epilogue: /^epilogue\s*[:\-–—]?\s*/i,
  introduction: /^introduction\s*[:\-–—]?\s*/i,
  preface: /^preface\s*[:\-–—]?\s*/i,
};

function detectSectionType(title: string): ParsedChapter['sectionType'] {
  const lowerTitle = title.toLowerCase().trim();

  for (const [type, pattern] of Object.entries(sectionPatterns)) {
    if (pattern.test(lowerTitle)) {
      return type as ParsedChapter['sectionType'];
    }
  }

  return 'chapter';
}

function cleanHtmlContent(html: string): string {
  // Remove excessive whitespace but keep structure
  return html
    .replace(/<p>\s*<\/p>/g, '') // Remove empty paragraphs
    .replace(/\s+/g, ' ') // Normalize whitespace
    .trim();
}

function extractTextFromHtml(html: string): string {
  // Simple HTML to plain text for title detection
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

function isChapterHeading(text: string): boolean {
  const trimmed = text.trim();

  // Must be relatively short to be a heading (max ~100 chars)
  if (trimmed.length > 100) {
    return false;
  }

  // Check chapter patterns - must start with pattern
  for (const pattern of chapterPatterns) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }

  // Check section patterns
  for (const pattern of Object.values(sectionPatterns)) {
    if (pattern.test(trimmed)) {
      return true;
    }
  }

  return false;
}

export async function parseDocx(file: File): Promise<ParsedBook> {
  const arrayBuffer = await file.arrayBuffer();

  const result = await mammoth.convertToHtml(
    { arrayBuffer },
    {
      styleMap: [
        "p[style-name='Heading 1'] => h1:fresh",
        "p[style-name='Heading 2'] => h2:fresh",
        "p[style-name='Title'] => h1.title:fresh",
      ],
    }
  );

  const html = result.value;
  const messages = result.messages;

  // Extract images for cover detection
  let coverImage: string | undefined;
  const imageMessages = messages.filter(m => m.type === 'warning' && m.message.includes('image'));

  // Try to extract images using mammoth's image converter
  try {
    const imageResult = await mammoth.convertToHtml(
      { arrayBuffer },
      {
        convertImage: mammoth.images.imgElement(async (image) => {
          const imageBuffer = await image.read();
          const base64 = btoa(
            new Uint8Array(imageBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
          );
          const mimeType = image.contentType || 'image/png';

          // Use first image as potential cover
          if (!coverImage) {
            coverImage = `data:${mimeType};base64,${base64}`;
          }

          return { src: `data:${mimeType};base64,${base64}` };
        }),
      }
    );
  } catch {
    // Ignore image extraction errors
  }

  // Parse HTML to find chapter breaks
  const chapters: ParsedChapter[] = [];
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Find all headings and paragraphs
  const elements = doc.body.children;
  let currentChapter: { title: string; content: string[]; sectionType: ParsedChapter['sectionType'] } | null = null;
  let chapterCount = 0;

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const tagName = element.tagName.toLowerCase();
    const text = extractTextFromHtml(element.outerHTML);

    // Check if this is a chapter heading
    // Only h1 is a chapter break, h2+ are subheadings within chapters
    // Or paragraphs that CLEARLY match chapter patterns (Chapter 1, etc.)
    const isH1 = tagName === 'h1';
    const isChapterParagraph = tagName === 'p' && isChapterHeading(text) && text.length < 80;
    const isChapter = isH1 || isChapterParagraph;

    if (isChapter && text.length > 0 && text.length < 150) {
      // Save previous chapter
      if (currentChapter && currentChapter.content.length > 0) {
        chapters.push({
          title: currentChapter.title,
          content: currentChapter.content.join(''),
          sectionType: currentChapter.sectionType,
        });
      }

      // Start new chapter
      chapterCount++;
      const sectionType = detectSectionType(text);
      let title = text;

      // Clean up chapter title
      for (const pattern of chapterPatterns) {
        title = title.replace(pattern, '').trim();
      }
      for (const pattern of Object.values(sectionPatterns)) {
        title = title.replace(pattern, '').trim();
      }

      if (!title || title.length === 0) {
        title = sectionType === 'chapter' ? `Chapter ${chapterCount}` : sectionType.charAt(0).toUpperCase() + sectionType.slice(1);
      }

      currentChapter = {
        title,
        content: [],
        sectionType,
      };
    } else if (currentChapter) {
      // Add content to current chapter
      currentChapter.content.push(element.outerHTML);
    } else {
      // Content before first chapter - create initial chapter
      if (!currentChapter) {
        chapterCount++;
        currentChapter = {
          title: `Chapter ${chapterCount}`,
          content: [element.outerHTML],
          sectionType: 'chapter',
        };
      }
    }
  }

  // Save last chapter
  if (currentChapter && currentChapter.content.length > 0) {
    chapters.push({
      title: currentChapter.title,
      content: currentChapter.content.join(''),
      sectionType: currentChapter.sectionType,
    });
  }

  // If no chapters found, create single chapter with all content
  if (chapters.length === 0 && html.trim().length > 0) {
    chapters.push({
      title: 'Chapter 1',
      content: html,
      sectionType: 'chapter',
    });
  }

  // Try to extract title from filename
  const fileName = file.name.replace(/\.docx$/i, '');
  const title = fileName.replace(/[-_]/g, ' ').trim();

  return {
    chapters,
    coverImage,
    title: title || undefined,
  };
}
